/**
 * BunkeringTab Component
 * Handles bunkering fuel card transactions including Key Fuels
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Fuel, Plus, X } from 'lucide-react';

export const BunkeringTab = ({
  tillLock = false,
  storeDetails,
  filteredActiveFuelCards,
  fuelCardTransactions,
  setFuelCardTransactions,
  fuelPrices
}) => {
  return (
    <fieldset disabled={tillLock} className="space-y-3 min-w-0 border-0 p-0 m-0">
      {tillLock && (
        <div className="flex items-center gap-2 p-2 rounded-lg border border-blue-500/30 bg-blue-500/10 text-sm text-blue-600 dark:text-blue-400" data-testid="till-lock-banner-bunkering">
          These bunkering lines were filled from the Day End report and are read-only.
        </div>
      )}
      {/* Note: Non-Bunkering cards (Allstar, UK Fuel, DKV) are now entered on the Tenders tab */}

      {/* Bunkering Cards Section (includes Key Fuels) - Compact */}
      {(filteredActiveFuelCards?.some(c => c.category === 'bunkering') || filteredActiveFuelCards?.some(c => c.category === 'key_fuels')) && (
        <Card>
          <CardHeader className="bg-orange-500/5 border-b border-orange-500/20 py-2 px-4">
            <CardTitle className="flex items-center gap-2 text-base text-orange-600" style={{ fontFamily: 'Barlow Condensed' }}>
              <Fuel className="h-4 w-4" />
              Bunkering Cards
              <span className="text-xs text-muted-foreground font-normal">(Cost + Fee)</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="py-3 px-4 space-y-3">
            {/* Regular Bunkering Cards */}
            {filteredActiveFuelCards
              .filter(c => c.category === 'bunkering')
              .map((card) => {
                const cardTx = fuelCardTransactions.bunkering[card.card_name] || { transactions: [], total_amount: 0 };
                return (
                  <div key={card.card_type_id} className="border rounded-lg p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-sm text-orange-600">{card.card_name}</h4>
                      <span className="text-xs text-muted-foreground">Fee: {card.handling_fee_per_litre || 0.5}p/L</span>
                    </div>
                    
                    {/* Transactions list - Compact */}
                    {cardTx.transactions.map((tx, idx) => (
                      <div key={idx} className="grid grid-cols-5 gap-2 items-center">
                        <Select
                          value={tx.grade_id}
                          onValueChange={(value) => {
                            const grade = storeDetails.fuel_grades.find(g => g.id === value);
                            const newTx = [...cardTx.transactions];
                            newTx[idx] = { 
                              ...newTx[idx], 
                              grade_id: value, 
                              grade_name: grade?.name || '',
                              selling_price: parseFloat(fuelPrices[value]) || grade?.selling_price || 0
                            };
                            delete newTx[idx].unit_price;
                            newTx[idx].amount = (newTx[idx].volume || 0) * (newTx[idx].selling_price / 100);
                            setFuelCardTransactions({
                              ...fuelCardTransactions,
                              bunkering: {
                                ...fuelCardTransactions.bunkering,
                                [card.card_name]: {
                                  transactions: newTx,
                                  total_amount: newTx.reduce((s, t) => s + (t.amount || 0), 0)
                                }
                              }
                            });
                          }}
                        >
                          <SelectTrigger className="h-7 text-xs">
                            <SelectValue placeholder="Grade" />
                          </SelectTrigger>
                          <SelectContent>
                            {(storeDetails.fuel_grades || []).map(grade => (
                              <SelectItem key={grade.id} value={grade.id}>{grade.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Input
                          type="number"
                          placeholder="Vol"
                          value={tx.volume || ''}
                          autoComplete="off"
                          onChange={(e) => {
                            const volume = parseFloat(e.target.value) || 0;
                            const newTx = [...cardTx.transactions];
                            newTx[idx] = { ...newTx[idx], volume };
                            newTx[idx].amount = volume * (newTx[idx].unit_price ?? ((newTx[idx].selling_price || 0) / 100));
                            setFuelCardTransactions({
                              ...fuelCardTransactions,
                              bunkering: {
                                ...fuelCardTransactions.bunkering,
                                [card.card_name]: {
                                  transactions: newTx,
                                  total_amount: newTx.reduce((s, t) => s + (t.amount || 0), 0)
                                }
                              }
                            });
                          }}
                          className="h-7 text-xs font-mono text-right"
                        />
                        <div className="text-xs text-center text-muted-foreground">
                          {tx.selling_price || '-'}p
                        </div>
                        <div className="text-xs font-mono text-right font-medium">
                          £{(tx.amount || 0).toFixed(2)}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-destructive disabled:opacity-30"
                          onClick={() => {
                            const newTx = cardTx.transactions.filter((_, i) => i !== idx);
                            setFuelCardTransactions({
                              ...fuelCardTransactions,
                              bunkering: {
                                ...fuelCardTransactions.bunkering,
                                [card.card_name]: {
                                  transactions: newTx,
                                  total_amount: newTx.reduce((s, t) => s + (t.amount || 0), 0)
                                }
                              }
                            });
                          }}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                    
                    <div className="flex items-center justify-between">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => {
                          setFuelCardTransactions({
                            ...fuelCardTransactions,
                            bunkering: {
                              ...fuelCardTransactions.bunkering,
                              [card.card_name]: {
                                ...cardTx,
                                transactions: [...cardTx.transactions, { grade_id: '', grade_name: '', volume: 0, selling_price: 0, amount: 0 }]
                              }
                            }
                          });
                        }}
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        Add
                      </Button>
                      <span className="text-sm font-medium text-orange-600">
                        {card.card_name} Total: £{(cardTx.total_amount || 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                );
              })}

            {/* Key Fuels Section - Merged into Bunkering */}
            {filteredActiveFuelCards?.some(c => c.category === 'key_fuels') && (
              <div className="border rounded-lg p-3 space-y-2 border-green-500/30">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold text-sm text-green-600">Key Fuels</h4>
                  <span className="text-xs text-muted-foreground">(Diesel: Cost + Fee | Others: Retail - %)</span>
                </div>
                
                {/* Key Fuels Transactions - Compact */}
                {fuelCardTransactions.key_fuels.transactions.map((tx, idx) => (
                  <div key={idx} className="grid grid-cols-5 gap-2 items-center">
                    <Select
                      value={tx.grade_id}
                      onValueChange={(value) => {
                        const grade = storeDetails.fuel_grades.find(g => g.id === value);
                        const newTx = [...fuelCardTransactions.key_fuels.transactions];
                        const isDiesel = grade?.name?.toLowerCase().includes('diesel');
                        newTx[idx] = { 
                          ...newTx[idx], 
                          grade_id: value, 
                          grade_name: grade?.name || '',
                          selling_price: parseFloat(fuelPrices[value]) || grade?.selling_price || 0,
                          is_diesel: isDiesel
                        };
                        delete newTx[idx].unit_price;
                            newTx[idx].amount = (newTx[idx].volume || 0) * (newTx[idx].selling_price / 100);
                        setFuelCardTransactions({
                          ...fuelCardTransactions,
                          key_fuels: {
                            ...fuelCardTransactions.key_fuels,
                            transactions: newTx,
                            total_amount: newTx.reduce((s, t) => s + (t.amount || 0), 0) + (parseFloat(fuelCardTransactions.key_fuels.shop_items_amount) || 0)
                          }
                        });
                      }}
                    >
                      <SelectTrigger className="h-7 text-xs">
                        <SelectValue placeholder="Grade" />
                      </SelectTrigger>
                      <SelectContent>
                        {(storeDetails.fuel_grades || []).map(grade => (
                          <SelectItem key={grade.id} value={grade.id}>
                            {grade.name} {grade.name?.toLowerCase().includes('diesel') ? '(Cost)' : '(Retail)'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      type="number"
                      placeholder="Vol"
                      value={tx.volume || ''}
                      autoComplete="off"
                      onChange={(e) => {
                        const volume = parseFloat(e.target.value) || 0;
                        const newTx = [...fuelCardTransactions.key_fuels.transactions];
                        newTx[idx] = { ...newTx[idx], volume };
                        newTx[idx].amount = volume * (newTx[idx].unit_price ?? ((newTx[idx].selling_price || 0) / 100));
                        setFuelCardTransactions({
                          ...fuelCardTransactions,
                          key_fuels: {
                            ...fuelCardTransactions.key_fuels,
                            transactions: newTx,
                            total_amount: newTx.reduce((s, t) => s + (t.amount || 0), 0) + (parseFloat(fuelCardTransactions.key_fuels.shop_items_amount) || 0)
                          }
                        });
                      }}
                      className="h-7 text-xs font-mono text-right"
                    />
                    <div className="text-xs text-center text-muted-foreground">
                      {tx.selling_price || '-'}p
                    </div>
                    <div className="text-xs font-mono text-right font-medium">
                      £{(tx.amount || 0).toFixed(2)}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 text-destructive disabled:opacity-30"
                      onClick={() => {
                        const newTx = fuelCardTransactions.key_fuels.transactions.filter((_, i) => i !== idx);
                        setFuelCardTransactions({
                          ...fuelCardTransactions,
                          key_fuels: {
                            ...fuelCardTransactions.key_fuels,
                            transactions: newTx,
                            total_amount: newTx.reduce((s, t) => s + (t.amount || 0), 0) + (parseFloat(fuelCardTransactions.key_fuels.shop_items_amount) || 0)
                          }
                        });
                      }}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
                
                {/* Shop Items - Compact */}
                <div className="flex items-center gap-2 pt-2 border-t">
                  <Label className="text-xs whitespace-nowrap">Shop Items:</Label>
                  <div className="relative flex-1 max-w-[100px]">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">£</span>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={fuelCardTransactions.key_fuels.shop_items_amount || ''}
                      onChange={(e) => {
                        const shopAmount = parseFloat(e.target.value) || 0;
                        setFuelCardTransactions({
                          ...fuelCardTransactions,
                          key_fuels: {
                            ...fuelCardTransactions.key_fuels,
                            shop_items_amount: e.target.value,
                            total_amount: fuelCardTransactions.key_fuels.transactions.reduce((s, t) => s + (t.amount || 0), 0) + shopAmount
                          }
                        });
                      }}
                      className="pl-5 h-7 text-xs font-mono"
                    />
                  </div>
                </div>
                
                <div className="flex items-center justify-between pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => {
                      setFuelCardTransactions({
                        ...fuelCardTransactions,
                        key_fuels: {
                          ...fuelCardTransactions.key_fuels,
                          transactions: [...fuelCardTransactions.key_fuels.transactions, { grade_id: '', grade_name: '', volume: 0, selling_price: 0, amount: 0, is_diesel: false }]
                        }
                      });
                    }}
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Add Grade
                  </Button>
                  <span className="text-sm font-medium text-green-600">
                    Key Fuels Total: £{(fuelCardTransactions.key_fuels.total_amount || 0).toFixed(2)}
                  </span>
                </div>
              </div>
            )}
            
            {/* Bunkering Total (Bunkering Cards + Key Fuels) */}
            <div className="bg-orange-500/10 rounded-lg p-3 mt-2">
              <div className="flex justify-between items-center">
                <span className="text-sm font-semibold text-orange-600">Bunkering Total:</span>
                <span className="text-lg font-bold text-orange-600">
                  £{(
                    Object.values(fuelCardTransactions.bunkering).reduce((sum, card) => sum + (card.total_amount || 0), 0) +
                    (fuelCardTransactions.key_fuels.total_amount || 0)
                  ).toFixed(2)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Fuel Cards Summary - Bunkering Total only (Non-Bunkering is on Tenders tab) */}
      <Card className="bg-orange-500/5 border-orange-500/30">
        <CardContent className="pt-4">
          <div className="flex justify-between items-center">
            <span className="text-lg font-semibold">Bunkering Total:</span>
            <span className="text-2xl font-bold text-orange-600">
              £{(
                Object.values(fuelCardTransactions.bunkering).reduce((sum, card) => sum + (card.total_amount || 0), 0) +
                (fuelCardTransactions.key_fuels.total_amount || 0)
              ).toFixed(2)}
            </span>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Non-Bunkering cards (Allstar, UK Fuel, DKV) are entered on the Tenders tab
          </p>
        </CardContent>
      </Card>
    </fieldset>
  );
};
